"use client";
import { useCallback, useRef, useState } from "react";
import { invalidateQueries } from "../useQuery/cache";
import type {
  GraphqlError,
  GraphqlResult,
  VariablesArgs,
} from "../useQuery/GraphqlResult";
import toGraphqlError from "../useQuery/toGraphqlError";

/**
 * A `.client` getter: `getXMutationClient(variables)`.
 */
type Fetcher<Data, Variables> = (
  variables: Variables,
) => Promise<GraphqlResult<Data>>;

export type MutationOptions = {
  /**
   * Query getters whose cached results this mutation makes stale. After a
   * call that returns data, they are invalidated and refetched wherever
   * mounted.
   */
  refetchQueries?: ReadonlyArray<
    (variables: never) => Promise<GraphqlResult<unknown>>
  >;
};

type State<Data> = {
  data?: Data;
  loading: boolean;
  error?: GraphqlError;
  called: boolean;
};

/**
 * Runs a `.client` mutation from a client component. Prefer the generated
 * `useXMutation` hook of the operation, which binds the getter and the types.
 *
 * Returns the mutate function and the last call's state, like Apollo's
 * `useMutation`. Queries that read what the mutation changed go in
 * `refetchQueries`; data the server renders needs `router.refresh()`.
 *
 * @example
 * const [toggleBonus, { loading }] = useToggleBonusMutation({
 *   refetchQueries: [getMyActiveRewardQueryClient],
 * });
 * const { data } = await toggleBonus({ id });
 */
const useMutation = <Data, Variables extends object>(
  fetcher: Fetcher<Data, Variables>,
  options?: MutationOptions,
) => {
  const [state, setState] = useState<State<Data>>({
    loading: false,
    called: false,
  });
  const requestId = useRef(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutate = useCallback(
    async (
      ...[variables]: VariablesArgs<Variables>
    ): Promise<GraphqlResult<Data>> => {
      const id = ++requestId.current;
      setState((previous) => ({ ...previous, loading: true, called: true }));
      const result = await fetcher((variables ?? {}) as Variables).catch(
        (error: unknown): GraphqlResult<Data> => ({
          data: undefined,
          error: "exception",
          errors: [{ message: error instanceof Error ? error.message : "" }],
        }),
      );
      if (id === requestId.current) {
        setState({
          data: result.data,
          loading: false,
          error: toGraphqlError(result),
          called: true,
        });
      }
      if (result.data !== undefined) {
        for (const query of optionsRef.current?.refetchQueries ?? []) {
          invalidateQueries(query);
        }
      }
      return result;
    },
    [fetcher],
  );

  return [mutate, state] as const;
};

export default useMutation;
